import {
  Box,
  ButtonDanger,
  ButtonIcon,
  ButtonOutline,
  DrawerItem,
  DrawerItemHeader,
  DrawerItemHint,
  Flex,
  Input,
  Switch
} from 'front-end/src/components'
import React, { ReactNode, useRef, useState } from 'react'
import { useClipboard } from 'use-clipboard-copy'

import { Portal } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { t, Trans } from '@lingui/macro'

import { brandColors } from '../../../../../shared/basicPalette'
import { linkBuilder, staticLinks } from '../../../../../shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import { AccessibilityLabel } from '../../../components/common/AccessibilityLabel'
import { Upload } from '../../../components/common/form/upload/Upload'
import { IconArchive } from '../../../components/common/icon/IconArchive'
import IconBubble from '../../../components/common/icon/IconBubble'
import { IconCircleEmpty } from '../../../components/common/icon/IconCircleEmpty'
import IconCopy from '../../../components/common/icon/IconCopy'
import IconLink from '../../../components/common/icon/IconLink'
import IconLock from '../../../components/common/icon/IconLock'
import IconPicture from '../../../components/common/icon/IconPicture'
import { IconSequence } from '../../../components/common/icon/IconSequence'
import IconTag from '../../../components/common/icon/IconTag'
import { notification } from '../../../components/common/notifications/Notifications'
import DrawerImage from '../../../components/common/page/drawer/DrawerImage'
import TagSelect, {
  TAG_TARGET_TYPES
} from '../../../components/common/tag/TagSelect'
import {
  PathwayInput,
  PathwayProgress,
  PathwayState
} from '../../../graphql/private/PrivateSchemaTypes'
import { PathwayDeleteModal } from '../../manage-pathways-page/components/PathwayDeleteModal'
import { PathwayUnpublishModal } from '../../manage-pathways-page/components/PathwayUnpublishModal'
import { useManagePathwayWithTopicsQuery } from '../ManagePathwayPage'
import {
  PathwayDetailFieldsForManageFragment,
  usePathwayPatchMutation
} from '../ManagePathwayPage.codegen'
import {
  usePathwayRemoveCoverImageMutation,
  usePathwayUploadCoverImageMutation
} from './PathwaySettingsDrawer.codegen'

const renderUploadField = (
  loading: boolean,
  onImageUpload: (file: any) => void
): ReactNode => {
  return (
    <Upload
      title={t`Choose image`}
      accept="image/png, image/jpeg, image/gif"
      isLoading={loading}
      onChange={(e) => {
        const files = e.currentTarget.files
        if (files && files.length) {
          const file = files[0]
          onImageUpload(file)
        }
      }}
    />
  )
}

type FormState = Pick<
  PathwayInput,
  'progress' | 'allow_comments' | 'published_date'
>

export const PathwayGeneralDrawerContent = ({
  pathway,
  hideDrawer
}: {
  pathway: PathwayDetailFieldsForManageFragment
  hideDrawer: () => void
}) => {
  const { refetch } = useManagePathwayWithTopicsQuery({
    variables: {
      id: pathway.id
    }
  })
  const [patchPathway] = usePathwayPatchMutation()
  const [form, setForm] = useState<FormState>({
    progress: pathway.progress,
    allow_comments: pathway.allow_comments,
    published_date: pathway.published_date
  })
  const link = `${location.origin}${linkBuilder.pathwayDetail(pathway.id)}`
  const textAreaRef = useRef<HTMLInputElement>(null)
  const clipboard = useClipboard()
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [showUnpublishModal, setShowUnpublishModal] = useState(false)

  const updatePathway = async (patch: FormState) => {
    setForm({ ...form, ...patch })
    await patchPathway({ variables: { id: pathway.id, input: patch } })
    refetch()
  }

  // does variant image exists?
  let image
  const { coverImageVariants, coverImage } = pathway
  if (coverImageVariants && coverImageVariants.length > 0) {
    const variantImage = coverImageVariants.find(
      (image) => image.size_key === '300x200'
    )
    image = variantImage ? variantImage.cdnUrl : null
  } else {
    image = coverImage ? coverImage.cdnUrl : null
  }

  const [imageUpload] = usePathwayUploadCoverImageMutation()
  const [removeImage] = usePathwayRemoveCoverImageMutation()

  return (
    <>
      <DrawerItem>
        <DrawerItemHeader dataTest="AccessLinkDrawer">
          <IconLink color={brandColors.grey['500']} />
          <Trans>Access link</Trans>
        </DrawerItemHeader>

        <Flex align="center">
          <Input
            variant="filled"
            ref={textAreaRef}
            value={link}
            readOnly
            css={css`
              vertical-align: middle;
              height: 40px;
              flex: 1;
              font-size: 12px;
              line-height: 15px;
            `}
          />
          <Box mx="8px">
            <ButtonIcon
              hint={t`Copy to clipboard`}
              aria-label={t`Copy to clipboard`}
              onClick={() => {
                clipboard.copy(link)
                notification.info(t`Successfully copied to clipboard`)
              }}
              icon={<IconCopy boxSize="16px" />}
              data-test="CopyButton"
            />
          </Box>
        </Flex>

        <DrawerItemHint>
          <Trans>Available to current users with access</Trans>
        </DrawerItemHint>
      </DrawerItem>

      <DrawerItem>
        <Flex align="center" justify="space-between">
          <DrawerItemHeader mb={0} dataTest="EnforcePathwayOrderDrawer">
            <IconSequence color={brandColors.grey['500']} />
            <Trans>Enforce pathway order</Trans>
          </DrawerItemHeader>

          <Switch
            isChecked={
              form.progress === PathwayProgress.linear ||
              form.progress === PathwayProgress.locked
            }
            isDisabled={form.progress === PathwayProgress.locked}
            onChange={() =>
              updatePathway({
                progress:
                  pathway.progress === PathwayProgress.open
                    ? PathwayProgress.linear
                    : PathwayProgress.open
              })
            }
          />
        </Flex>

        <DrawerItemHint>
          <Trans>
            Enforcing pathway order requires people to complete the resources in
            the order you define.
          </Trans>
        </DrawerItemHint>
      </DrawerItem>

      {(pathway.progress === PathwayProgress.linear ||
        pathway.progress === PathwayProgress.locked) && (
        <DrawerItem>
          <Flex align="center" justify="space-between">
            <DrawerItemHeader mb={0} dataTest="LockUnlessPassedDrawer">
              <IconLock color={brandColors.grey['500']} />
              <Trans>Lock unless passed</Trans>
            </DrawerItemHeader>

            <Switch
              isChecked={pathway.progress === PathwayProgress.locked}
              onChange={() =>
                updatePathway({
                  progress:
                    pathway.progress === PathwayProgress.locked
                      ? PathwayProgress.linear
                      : PathwayProgress.locked
                })
              }
            />
          </Flex>

          <DrawerItemHint>
            <Trans>
              Only unlock the next resource for a user if the previous resource
              is successfully passed.
            </Trans>
          </DrawerItemHint>
        </DrawerItem>
      )}

      <DrawerItem>
        <DrawerItemHeader>
          <IconPicture color={brandColors.grey['500']} />
          <Trans>Cover image</Trans>
        </DrawerItemHeader>
        {image && (
          <DrawerImage imageUrl={image}>
            <ButtonDanger
              onClick={async () => {
                const { data } = await removeImage({
                  variables: { id: pathway.id }
                })
                // @ts-expect-error
                onChange(data?.admin?.pathway?.removeCoverImage)
              }}
            >
              <Trans>Remove image</Trans>
            </ButtonDanger>
          </DrawerImage>
        )}
        {renderUploadField(false, (file) => {
          // TODO
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = async (): Promise<void> => {
            const { result } = reader
            if (result) {
              const { data } = await imageUpload({
                variables: {
                  id: pathway.id,
                  file: {
                    name: file.name,
                    data: result as string
                  }
                }
              })
              // @ts-expect-error
              onChange(data?.admin?.pathway?.uploadCoverImage)
            } else {
              notification.error(t`There was a problem reading the file`)
            }
          }
        })}
      </DrawerItem>

      <DrawerItem isInline>
        <DrawerItemHeader mb={0} dataTest="AllowCommentsDrawer">
          <IconBubble color={brandColors.grey['500']} />
          <Trans>Allow comments</Trans>
        </DrawerItemHeader>
        <Switch
          isChecked={!!form.allow_comments}
          onChange={async () => {
            updatePathway({
              allow_comments: !form.allow_comments
            })
          }}
        />
      </DrawerItem>

      <DrawerItem>
        <DrawerItemHeader dataTest="AllowCommentsDrawer">
          <IconTag color={brandColors.grey['500']} />
          <Trans>Tags</Trans>
        </DrawerItemHeader>
        <AccessibilityLabel text={t`Tags`}>
          <TagSelect
            target={{
              id: pathway.id,
              type: TAG_TARGET_TYPES.pathway
            }}
            defaultValue={pathway.tags}
          />
        </AccessibilityLabel>
        <DrawerItemHint>
          <Trans>Type to create new tags</Trans>
        </DrawerItemHint>
      </DrawerItem>

      <DrawerItem>
        {pathway.state === PathwayState.published && (
          <ButtonOutline
            justifyContent="flex-start"
            mb="12px"
            px="12px"
            onClick={() => setShowUnpublishModal(true)}
            isFullWidth
            leftIcon={<IconCircleEmpty />}
          >
            <Trans>Return pathway to draft</Trans>
          </ButtonOutline>
        )}

        <ButtonOutline
          px="12px"
          color={brandColors.red.mid}
          justifyContent="flex-start"
          onClick={() => setShowRemoveModal(true)}
          isFullWidth
          leftIcon={<IconArchive />}
        >
          <Trans>Archive pathway</Trans>
        </ButtonOutline>
      </DrawerItem>

      <Portal>
        {showRemoveModal && (
          <PathwayDeleteModal
            show={showRemoveModal}
            pathwayId={pathway.id}
            onCanceled={() => setShowRemoveModal(false)}
            onDeleted={() => {
              navigateTo(staticLinks.managePathways)
            }}
          />
        )}

        {showUnpublishModal && (
          <PathwayUnpublishModal
            show={showUnpublishModal}
            pathwayId={pathway.id}
            onCanceled={() => setShowUnpublishModal(false)}
            onSubmitted={async () => {
              await updatePathway({ published_date: null })
              setShowUnpublishModal(false)
              hideDrawer()
            }}
          />
        )}
      </Portal>
    </>
  )
}
